import { useEffect, useMemo, useState } from "react";
import { ConnectButton } from "@rainbow-me/rainbowkit";
import {
  useAccount,
  useReadContracts,
  useSwitchChain,
  useWaitForTransactionReceipt,
  useWriteContract,
} from "wagmi";
import { formatEther, type Address, type Chain } from "viem";
import { collectionAbi, PHASES } from "./abi";
import {
  collectionName,
  collections,
  isConfigured,
  targetChain,
  type CollectionConfig,
} from "./config";

const ZERO_ADDRESS = "0x0000000000000000000000000000000000000000" as Address;

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="stat">
      <span className="stat-label">{label}</span>
      <span className="stat-value">{value}</span>
    </div>
  );
}

function CollectionCard({ collection }: { collection: CollectionConfig }) {
  const { address, isConnected, chainId } = useAccount();
  const { switchChain } = useSwitchChain();
  const [quantity, setQuantity] = useState(1);
  const [proofText, setProofText] = useState("");

  const { data, refetch } = useReadContracts({
    contracts: [
      { address: collection.address, abi: collectionAbi, functionName: "phase" },
      { address: collection.address, abi: collectionAbi, functionName: "mintPrice" },
      { address: collection.address, abi: collectionAbi, functionName: "allowlistPrice" },
      { address: collection.address, abi: collectionAbi, functionName: "maxPerWallet" },
      { address: collection.address, abi: collectionAbi, functionName: "totalSupply" },
      { address: collection.address, abi: collectionAbi, functionName: "maxSupply" },
      {
        address: collection.address,
        abi: collectionAbi,
        functionName: "mintedBy",
        args: [address ?? ZERO_ADDRESS],
      },
    ],
    query: { enabled: isConfigured, refetchInterval: 8000 },
  });

  const read = <T,>(i: number): T | undefined => data?.[i]?.result as T | undefined;

  const phase = Number(read<number>(0) ?? 0);
  const mintPrice = read<bigint>(1) ?? 0n;
  const allowlistPrice = read<bigint>(2) ?? 0n;
  const maxPerWallet = read<bigint>(3) ?? 0n;
  const totalSupply = read<bigint>(4) ?? 0n;
  const maxSupply = read<bigint>(5) ?? 0n;
  const myMinted = read<bigint>(6) ?? 0n;

  const { writeContract, data: hash, isPending, error: writeError } = useWriteContract();
  const {
    isLoading: confirming,
    isSuccess,
    error: receiptError,
  } = useWaitForTransactionReceipt({ hash });

  useEffect(() => {
    if (isSuccess) void refetch();
  }, [isSuccess, refetch]);

  const proof = useMemo(
    () =>
      proofText
        .split(/[\s,]+/)
        .map((s) => s.trim())
        .filter((s) => /^0x[a-fA-F0-9]{64}$/.test(s)) as `0x${string}`[],
    [proofText],
  );

  const wrongNetwork = isConnected && chainId !== collection.chain.id;
  const remaining = maxSupply - totalSupply;
  const soldOut = maxSupply > 0n && remaining <= 0n;
  const unitPrice = phase === 1 ? allowlistPrice : mintPrice;
  const totalCost = unitPrice * BigInt(quantity);
  const limitReached = maxPerWallet > 0n && myMinted >= maxPerWallet;
  const error = writeError ?? receiptError;
  const busy = isPending || confirming;
  const free = unitPrice === 0n;
  // Never offer a quantity the contract will reject.
  const qtyOptions = [1, 2, 3, 5].filter(
    (n) => maxPerWallet === 0n || BigInt(n) <= maxPerWallet,
  );
  const qtyChoices = qtyOptions.length ? qtyOptions : [1];

  function handleMint() {
    if (phase === 2) {
      writeContract({
        address: collection.address,
        abi: collectionAbi,
        functionName: "mintPublic",
        args: [BigInt(quantity)],
        value: mintPrice * BigInt(quantity),
      });
    } else if (phase === 1) {
      writeContract({
        address: collection.address,
        abi: collectionAbi,
        functionName: "mintAllowlist",
        args: [1n, proof],
        value: allowlistPrice,
      });
    }
  }

  return (
    <section className="panel">
      <div className="card-head">
        <div className="art" aria-hidden>
          🐱
        </div>
        <div>
          <h2>{collection.name}</h2>
          <p className="muted">
            {collection.chain.name} · {PHASES[phase] ?? "—"}
            {free && phase === 2 ? " · Free" : ""}
          </p>
        </div>
      </div>

      <div className="stats">
        <Stat label="Minted" value={`${totalSupply} / ${maxSupply}`} />
        <Stat
          label="Price"
          value={
            phase === 1
              ? `${formatEther(allowlistPrice)} ETH`
              : free
                ? "Free"
                : `${formatEther(mintPrice)} ETH`
          }
        />
        <Stat label="You minted" value={`${myMinted}${maxPerWallet > 0n ? ` / ${maxPerWallet}` : ""}`} />
        <Stat label="Supply left" value={remaining > 0n ? remaining.toString() : "0"} />
      </div>

      <div className="controls">
        {wrongNetwork && (
          <button className="btn primary" onClick={() => switchChain({ chainId: collection.chain.id })}>
            Switch to {collection.chain.name}
          </button>
        )}

        {!wrongNetwork && !isConnected && <p className="muted center">Connect to mint.</p>}

        {!wrongNetwork && isConnected && soldOut && <p className="muted center">Sold out.</p>}

        {!wrongNetwork && isConnected && !soldOut && phase === 0 && (
          <p className="muted center">Mint isn’t open yet.</p>
        )}

        {!wrongNetwork && isConnected && !soldOut && phase === 1 && (
          <>
            <label className="field">
              <span>Allowlist proof (one 32-byte hash per line)</span>
              <textarea
                rows={3}
                value={proofText}
                onChange={(e) => setProofText(e.target.value)}
                placeholder={"0xabc…\n0xdef…"}
              />
            </label>
            <button
              className="btn primary"
              disabled={busy || limitReached || proof.length === 0}
              onClick={handleMint}
            >
              {busy ? "Minting…" : `Mint allowlist · ${formatEther(allowlistPrice)} ETH`}
            </button>
          </>
        )}

        {!wrongNetwork && isConnected && !soldOut && phase === 2 && (
          <>
            <div className="qty">
              <span>Quantity</span>
              <div className="qty-buttons">
                {qtyChoices.map((n) => (
                  <button
                    key={n}
                    className={`chip ${quantity === n ? "on" : ""}`}
                    onClick={() => setQuantity(n)}
                    disabled={busy}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>

            <button
              className="btn primary"
              disabled={busy || limitReached || (maxSupply > 0n && BigInt(quantity) > remaining)}
              onClick={handleMint}
            >
              {busy ? "Minting…" : free ? `Mint ${quantity} · Free` : `Mint ${quantity} · ${formatEther(totalCost)} ETH`}
            </button>
          </>
        )}

        {limitReached && <p className="muted center">Wallet limit reached.</p>}
        {isSuccess && <p className="ok center">Minted! ✓</p>}
        {error && <p className="err center">{shortError(error)}</p>}
      </div>

      <a
        className="contract-link muted"
        href={explorerUrl(collection.address, collection.chain)}
        target="_blank"
        rel="noreferrer"
      >
        {shortAddr(collection.address)}
      </a>
    </section>
  );
}

export default function App() {
  return (
    <div className="wrap">
      <header className="bar">
        <div className="brand">
          <span className="glyph">◈</span>
          <div>
            <h1>{collectionName}</h1>
            <p className="muted">333 hand-made clay cats · art reveals the moment you mint</p>
          </div>
        </div>
        <ConnectButton showBalance={false} />
      </header>

      {!isConfigured && (
        <div className="banner warn">
          No collections configured. Deploy a contract, then set <code>VITE_CONTRACT_ADDRESS</code>{" "}
          (single) or <code>VITE_COLLECTIONS</code> (multiple) in <code>.env</code>.
        </div>
      )}

      <main className="grid">
        {collections.map((c) => (
          <CollectionCard key={c.address} collection={c} />
        ))}
      </main>

      <footer className="foot muted">
        <span>{targetChain.name}</span>
      </footer>
    </div>
  );
}

function shortAddr(a: string) {
  return `${a.slice(0, 6)}…${a.slice(-4)}`;
}

function explorerUrl(a: string, chain: Chain) {
  const base = chain.blockExplorers?.default?.url ?? "";
  return `${base}/address/${a}`;
}

function shortError(e: unknown) {
  const msg = e instanceof Error ? e.message : String(e);
  return msg.split("\n")[0].slice(0, 160);
}
